/* Desktop mega menu, the menu sheet below 1200px, and the header utility pop-over (LDJ header).
   Lives outside theme.js on purpose: theme.js holds the search machinery.

   - Mouse: hover intent. The first panel waits a beat (OPEN_DELAY). With a panel open,
     moving to a neighbour swaps after SWITCH_DELAY, but if the pointer is heading down
     (towards the open panel) the swap waits AIM_DELAY and is cancelled the moment the
     pointer reaches that panel, so a diagonal path to the promo tiles never hijacks or
     closes it. A pointer still sweeping fast over a neighbour (FLICK_SPEED) never swaps:
     on wide screens the far tiles sit at a shallow angle, and the swap waits until the
     pointer settles. Slipping off a panel is forgiven for CLOSE_DELAY.
   - Touch / pen on desktop widths: first tap opens the panel, second tap follows the link.
   - Keyboard: focus never opens a panel (Tab walks the top row). The toggle button next
     to each link (or ArrowDown on the link) opens it; ArrowDown moves into it; Escape
     closes and returns focus; tabbing out of an item closes its panel.
   - Promo photos start loading on the first sign of intent and fade in when ready.
   - Theme editor: selecting a "Menu image" block opens its panel. */
(() => {
  const DESKTOP = window.matchMedia('(min-width: 1200px)');
  const OPEN_DELAY = 120;
  const SWITCH_DELAY = 60;
  const AIM_DELAY = 220;
  const CLOSE_DELAY = 300;
  const FLICK_SPEED = 1; // px per ms

  const initNav = (scope) => {
    const nav = scope.querySelector('[data-site-nav]');
    if (!nav || nav.hasAttribute('data-nav-ready')) return;
    nav.setAttribute('data-nav-ready', '');

    const header = nav.closest('[data-site-header]');
    const items = Array.from(nav.querySelectorAll('[data-nav-item]'));
    const move = { x: 0, y: 0, dx: 0, dy: 0, t: 0 };
    let current = null;
    let pending = null; // the neighbour waiting to take over from the open panel
    let openTimer;
    let closeTimer;
    let lastPointer = 'mouse';
    let warmed = false;

    const linkOf = (item) => item.querySelector('.site-nav__link');
    const toggleOf = (item) => item.querySelector('[data-nav-toggle]');
    const controlOf = (item) => toggleOf(item) || linkOf(item);
    const panelOf = (item) => item.querySelector('[data-nav-panel]');

    // Closed panels are content-visibility: hidden, so their photos would only start
    // downloading on first open (grey tiles for a second on busy pages). Start them now.
    const warm = () => {
      if (warmed) return;
      warmed = true;
      nav.querySelectorAll('.site-nav__promo img').forEach((img) => {
        if (!img.complete) {
          img.classList.add('is-loading');
          const done = () => img.classList.remove('is-loading');
          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });
        }
        img.loading = 'eager';
      });
    };

    // Closed panels are inert so their links leave the tab order immediately.
    const setState = (item, isOpen) => {
      item.classList.toggle('is-open', isOpen);
      controlOf(item)?.setAttribute('aria-expanded', String(isOpen));
      const panel = panelOf(item);
      if (panel) panel.inert = !isOpen;
    };

    items.forEach((item) => {
      const panel = panelOf(item);
      if (panel) panel.inert = true;
    });

    const open = (item) => {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
      pending = null;
      if (current === item) return;
      warm();
      const switching = Boolean(current);
      if (switching) {
        // Swap panels without cross-fading two white sheets over each other.
        header?.classList.add('is-nav-switching');
        setState(current, false);
      }
      current = item;
      setState(item, true);
      header?.classList.add('has-open-panel');
      if (switching) {
        requestAnimationFrame(() => requestAnimationFrame(() => header?.classList.remove('is-nav-switching')));
      }
    };

    const closeAll = () => {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
      pending = null;
      if (current) setState(current, false);
      current = null;
      header?.classList.remove('has-open-panel');
    };

    const scheduleClose = () => {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(closeAll, CLOSE_DELAY);
    };

    // Pointer direction, smoothed over the last few moves and restarted after a pause (so the
    // trip up to the link doesn't count). Heading down at more than ~15 degrees = on the way
    // into the open panel; a sideways wobble of a pixel or two doesn't count.
    const aimingDown = () => move.dy > 0 && move.dy >= Math.abs(move.dx) * 0.25;

    const swapDelay = () => (aimingDown() ? AIM_DELAY : SWITCH_DELAY);

    nav.addEventListener('pointermove', (event) => {
      const dt = event.timeStamp - move.t;
      const dist = Math.hypot(event.clientX - move.x, event.clientY - move.y);
      const k = dt > 80 ? 1 : 0.5;
      move.dx = move.dx * (1 - k) + (event.clientX - move.x) * k;
      move.dy = move.dy * (1 - k) + (event.clientY - move.y) * k;
      move.x = event.clientX;
      move.y = event.clientY;
      move.t = event.timeStamp;
      // Still sweeping fast over a neighbour: start its wait again from here.
      if (pending && current && dt > 0 && dt <= 80 && dist / dt > FLICK_SPEED) {
        const item = pending;
        clearTimeout(openTimer);
        openTimer = setTimeout(() => open(item), swapDelay());
      }
    }, { passive: true });

    nav.addEventListener('pointerdown', (event) => { lastPointer = event.pointerType || 'mouse'; }, true);
    nav.addEventListener('keydown', () => { lastPointer = 'keyboard'; }, true);
    nav.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') warm(); });

    items.forEach((item) => {
      const link = linkOf(item);
      const toggle = toggleOf(item);

      item.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse' || !DESKTOP.matches) return;
        clearTimeout(closeTimer);
        clearTimeout(openTimer);
        if (current === item) {
          pending = null;
          return;
        }
        pending = current ? item : null;
        openTimer = setTimeout(() => open(item), current ? swapDelay() : OPEN_DELAY);
      });

      item.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse') return;
        clearTimeout(openTimer);
        if (pending === item) pending = null;
        if (current) scheduleClose();
      });

      // Touch and pen: the first tap opens the panel, the second follows the link.
      link?.addEventListener('click', (event) => {
        if (!DESKTOP.matches || (lastPointer !== 'touch' && lastPointer !== 'pen')) return;
        if (current !== item) {
          event.preventDefault();
          open(item);
        }
      });

      toggle?.addEventListener('click', () => {
        if (current === item) closeAll();
        else open(item);
      });

      // ArrowDown on the toggle (or on the link when there is no toggle) opens and enters.
      (toggle || link)?.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowDown' || !DESKTOP.matches) return;
        event.preventDefault();
        open(item);
        panelOf(item)?.querySelector('a')?.focus();
      });

      item.addEventListener('focusout', (event) => {
        if (current === item && !item.contains(event.relatedTarget) && lastPointer === 'keyboard') closeAll();
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !current) return;
      const item = current;
      const hadFocus = item.contains(document.activeElement);
      closeAll();
      if (hadFocus) controlOf(item)?.focus();
    });

    document.addEventListener('pointerdown', (event) => {
      if (current && !nav.contains(event.target)) closeAll();
    });

    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-search-modal-open], [data-header-drawer-open]')) closeAll();
    });

    DESKTOP.addEventListener?.('change', closeAll);

    nav.luxeOpenItem = open;
    nav.luxeCloseAll = closeAll;
  };

  const initPopovers = (scope) => {
    scope.querySelectorAll('[data-util-popover-toggle]').forEach((toggle) => {
      if (toggle.hasAttribute('data-popover-ready')) return;
      toggle.setAttribute('data-popover-ready', '');
      const popover = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!popover) return;

      const setOpen = (isOpen) => {
        popover.hidden = !isOpen;
        toggle.setAttribute('aria-expanded', String(isOpen));
        if (isOpen) popover.querySelector('a, button')?.focus({ preventScroll: true });
      };

      toggle.addEventListener('click', () => setOpen(popover.hidden));
      popover.querySelector('[data-util-popover-close]')?.addEventListener('click', () => {
        setOpen(false);
        toggle.focus();
      });
      document.addEventListener('pointerdown', (event) => {
        if (!popover.hidden && !popover.contains(event.target) && !toggle.contains(event.target)) setOpen(false);
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !popover.hidden) {
          setOpen(false);
          toggle.focus();
        }
      });
    });
  };

  /* Menu sheet (below 1200px). theme.js opens and closes it (.is-open on
     [data-header-drawer]); this adds the sliding panels, keeps focus inside the
     sheet, makes the page behind inert, and resets to the first panel on close. */
  const initDrawer = (scope) => {
    const drawer = scope.querySelector('[data-header-drawer]');
    if (!drawer || drawer.hasAttribute('data-mnav-ready')) return;
    drawer.setAttribute('data-mnav-ready', '');

    const root = drawer.querySelector('[data-mnav-root]');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    const SLIDE_MS = 380;
    let current = null;
    let opener = null;

    // What sits behind the sheet. The header's own row is left alone: theme.js hands
    // focus back to the menu button the moment the sheet closes.
    const pageParts = () => [
      document.getElementById('MainContent'),
      ...document.querySelectorAll('.shopify-section-group-footer-group, .shopify-section-group-header-group:not(.shopify-section-sticky-header)')
    ].filter(Boolean);

    const closePanel = (restoreFocus, instant) => {
      if (!current) return;
      const panel = current;
      const trigger = opener;
      current = null;
      opener = null;
      panel.classList.remove('is-active');
      drawer.classList.remove('is-deep');
      if (root) root.inert = false;
      trigger?.setAttribute('aria-expanded', 'false');
      const hide = () => {
        if (!panel.classList.contains('is-active')) panel.hidden = true;
      };
      if (instant || reduce.matches) hide();
      else window.setTimeout(hide, SLIDE_MS);
      if (restoreFocus) trigger?.focus({ preventScroll: true });
    };

    const openPanel = (panel, trigger) => {
      if (!panel || panel === current) return;
      if (current) closePanel(false, true);
      current = panel;
      opener = trigger;
      panel.hidden = false;
      panel.scrollTop = 0;
      trigger.setAttribute('aria-expanded', 'true');
      if (root) root.inert = true;
      // Paint the panel off-canvas first, then slide it in.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (current !== panel) return;
        panel.classList.add('is-active');
        drawer.classList.add('is-deep');
      }));
      panel.querySelector('[data-mnav-back]')?.focus({ preventScroll: true });
    };

    drawer.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-mnav-open]');
      if (trigger) {
        openPanel(document.getElementById(trigger.getAttribute('aria-controls')), trigger);
        return;
      }
      if (event.target.closest('[data-mnav-back]')) closePanel(true);
    });

    const focusables = () => Array.from(drawer.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'))
      .filter((el) => !el.closest('[hidden], [inert]') && el.getClientRects().length > 0);

    drawer.addEventListener('keydown', (event) => {
      if (!drawer.classList.contains('is-open')) return;
      if (event.key === 'Escape' && current) {
        // First Escape steps back one panel; the next reaches theme.js and closes the sheet.
        event.stopPropagation();
        closePanel(true);
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    let wasOpen = false;
    new MutationObserver(() => {
      const isOpen = drawer.classList.contains('is-open');
      if (isOpen === wasOpen) return;
      wasOpen = isOpen;
      // menu-lock also pauses the product page's smooth scrolling (assets/pdp-smooth.js).
      document.documentElement.classList.toggle('menu-lock', isOpen);
      pageParts().forEach((el) => {
        el.inert = isOpen;
      });
      if (isOpen) {
        // Warm the panels' tiles (lazy, and not shown yet) so they are there on the first tap.
        drawer.querySelectorAll('img[loading="lazy"]').forEach((img) => {
          img.loading = 'eager';
        });
      } else {
        // Back to the first panel once the sheet has slid away.
        window.setTimeout(() => {
          if (drawer.classList.contains('is-open')) return;
          closePanel(false, true);
          if (root) root.scrollTop = 0;
        }, reduce.matches ? 0 : 320);
      }
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });

    // Back/forward cache: never come back to a page with the sheet still open.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted && drawer.classList.contains('is-open')) {
        drawer.querySelector('[data-header-drawer-close]')?.click();
      }
    });
  };

  const init = (scope = document) => {
    initNav(scope);
    initPopovers(scope);
    initDrawer(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));

  document.addEventListener('shopify:block:select', (event) => {
    const item = event.target.closest('[data-nav-item]');
    const nav = event.target.closest('[data-site-nav]');
    if (item && nav?.luxeOpenItem) nav.luxeOpenItem(item);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    event.target.closest('[data-site-nav]')?.luxeCloseAll?.();
  });
})();
